'use client';

import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { Home, HomeOutlined, People } from '@mui/icons-material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

import { useCustomNavigate } from '@dh/hooks';
import { Column } from '@dh/styles';
import { BigSidenavItem, SmallSidenavItem } from '@dh/components';

import { ROUTES } from '../../../../constants';
import { Sidenav, SidenavProps } from '../Sidenav';

type MainAppSidenavProps = Omit<SidenavProps, 'children'> & {
    toggleDrawer: (bool?: boolean) => void;
};

// ============================================
// CONSTANTS - Outside component to prevent recreation
// ============================================

const MENU_ICONS = {
    home: {
        default: <HomeOutlined />,
        selected: <Home />
    },
    matching: {
        default: <LightbulbOutlinedIcon />,
        selected: <LightbulbIcon />
    },
    projects: {
        default: <CelebrationOutlinedIcon />,
        selected: <CelebrationIcon />
    },
    companies: {
        default: <PeopleOutlineIcon />,
        selected: <People />
    },
    faq: {
        default: <QuestionAnswerOutlinedIcon />,
        selected: <QuestionAnswerIcon />
    }
} as const;

// ============================================
// TYPES
// ============================================

interface MenuItem {
    key: string;
    icon: React.JSX.Element;
    selectedIcon: React.JSX.Element;
    title: string;
    target: string;
    selected: boolean;
}

interface MenuItemComponentProps {
    item: MenuItem;
    isOpen: boolean;
    onClick: (target: string, isSelected: boolean) => void;
}

// ============================================
// MEMOIZED MENU ITEM COMPONENT
// ============================================

const MenuItemComponent = React.memo<MenuItemComponentProps>(({ item, isOpen, onClick }) => {
    const handleClick = useCallback(() => {
        onClick(item.target, item.selected);
    }, [onClick, item.target, item.selected]);

    if (!isOpen) {
        return (
            <SmallSidenavItem
                icon={item.icon}
                label={item.title}
                selectedIcon={item.selectedIcon}
                isSelected={item.selected}
                handleOnClick={handleClick}
            />
        );
    }

    return (
        <BigSidenavItem
            icon={item.icon}
            selectedIcon={item.selectedIcon}
            title={item.title}
            isSelected={item.selected}
            handleOnClick={handleClick}
            fullWidth={false}
        />
    );
});

MenuItemComponent.displayName = 'MenuItemComponent';

// ============================================
// MAIN COMPONENT
// ============================================

const MainAppSidenavComponent: React.FC<MainAppSidenavProps> = ({
    isPermanent,
    isOpen,
    toggleDrawer
}) => {
    const router = useRouter();
    const { isProjectPath, isMatchingScreen, isCompaniesScreen, isHomeScreen, isFaqScreen } =
        useCustomNavigate();
    const t_sidenav = useTranslations('sidenav');

    // Memoized menu items data
    const menuItems = useMemo<MenuItem[]>(
        () => [
            {
                key: 'home',
                icon: MENU_ICONS.home.default,
                selectedIcon: MENU_ICONS.home.selected,
                title: t_sidenav('my_pmp'),
                target: ROUTES.Home,
                selected: isHomeScreen
            },
            {
                key: 'matching',
                icon: MENU_ICONS.matching.default,
                selectedIcon: MENU_ICONS.matching.selected,
                title: t_sidenav('matching'),
                target: ROUTES.Matching,
                selected: isMatchingScreen
            },
            {
                key: 'companies',
                icon: MENU_ICONS.companies.default,
                selectedIcon: MENU_ICONS.companies.selected,
                title: t_sidenav('companies'),
                target: ROUTES.Companies,
                selected: isCompaniesScreen
            },
            {
                key: 'faq',
                icon: MENU_ICONS.faq.default,
                selectedIcon: MENU_ICONS.faq.selected,
                title: t_sidenav('faq'),
                target: ROUTES.Faq,
                selected: isFaqScreen
            },
            {
                key: 'projects',
                icon: MENU_ICONS.projects.default,
                selectedIcon: MENU_ICONS.projects.selected,
                title: t_sidenav('projects'),
                target: ROUTES.Projects,
                selected: isProjectPath
            }
        ],
        [t_sidenav, isHomeScreen, isMatchingScreen, isProjectPath, isCompaniesScreen, isFaqScreen]
    );

    // Memoized click handler
    const handleMenuItemClick = useCallback(
        (target: string, isSelected: boolean) => {
            toggleDrawer(false);
            if (!isSelected) {
                router.push(target);
            }
        },
        [router, toggleDrawer]
    );

    return (
        <Sidenav isPermanent={isPermanent} isOpen={isOpen} toggleDrawer={toggleDrawer}>
            <Column gap={2} height={'100%'} justifyContent={'space-between'} mt={7}>
                {menuItems.map((item) => (
                    <MenuItemComponent
                        key={item.key}
                        item={item}
                        isOpen={isOpen}
                        onClick={handleMenuItemClick}
                    />
                ))}
            </Column>
        </Sidenav>
    );
};

// Memoize the entire component for additional performance
export const MainAppSidenav = React.memo(MainAppSidenavComponent);
